import { useState } from 'react';
import { MoreVertical, UserMinus, ArrowUpRight, Pin, ChevronDown, Sparkles, UserPlus } from 'lucide-react';
import { Badge, Button, ConfirmModal, RadixSelect, SelectContent, SelectItem, SelectTrigger, SelectValue, Table } from '@/components/ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { useSpaceContext } from '@/contexts/SpaceContext';
import { useAuth } from '@/store/auth-context';
import { useSortedMembers, useUpdateMemberRole, useRemoveMember, usePinMember } from '@/hooks/useMembers';
import { meetsRole } from '@/lib/space-roles';
import { displayName, initials } from '@/lib/user-display';
import { AddMemberModal } from './AddMemberModal';
import { AddViaRulesModal } from './AddViaRulesModal';
import { TransferOwnershipModal } from './TransferOwnershipModal';
import type { SpaceMembership, SpaceRole } from '@/types/spaces';

const ROLE_BADGE_VARIANT: Record<SpaceRole, 'purple' | 'info' | 'gray' | 'teal'> = {
  owner: 'purple',
  admin: 'info',
  member: 'teal',
  viewer: 'gray',
};

export function MembersTab() {
  const { spaceId, role: callerRole, space } = useSpaceContext();
  const { user } = useAuth();
  const { success, error: showError } = useToast();

  const { data: members = [], isLoading } = useSortedMembers(spaceId);
  const updateRole = useUpdateMemberRole(spaceId);
  const removeMember = useRemoveMember(spaceId);
  const pinMember = usePinMember(spaceId);

  const [addOpen, setAddOpen] = useState(false);
  const [addViaRulesOpen, setAddViaRulesOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<SpaceMembership | null>(null);

  const isAdminPlus = meetsRole(callerRole, 'admin');
  const isOwner = meetsRole(callerRole, 'owner');
  const ownerUserId = members.find((m) => m.role === 'owner')?.userId;

  const handleRoleChange = async (m: SpaceMembership, newRole: SpaceRole) => {
    if (newRole === m.role) return;
    if (newRole === 'owner') {
      // Server rejects role: 'owner' on PATCH; route through the transfer flow.
      setTransferOpen(true);
      return;
    }
    try {
      await updateRole.mutateAsync({ userId: m.userId, role: newRole });
      success(`Updated ${displayName(m)} to ${newRole}`);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update role');
    }
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    try {
      await removeMember.mutateAsync(removeTarget.userId);
      success('Member removed');
      setRemoveTarget(null);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to remove member');
    }
  };

  const handlePin = async (m: SpaceMembership) => {
    try {
      await pinMember.mutateAsync(m.userId);
      success(`${displayName(m)} is now a manual member — auto-join rules won't change them`);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to pin member');
    }
  };

  const columns = [
    {
      key: 'user',
      header: 'Member',
      render: (m: SpaceMembership) => {
        const isSelf = m.userId === user?.userId;
        const hasName = !!(m.firstName || m.lastName);
        return (
          <div className="flex items-center gap-3">
            {m.avatarUrl ? (
              <img
                src={m.avatarUrl}
                alt=""
                className="h-8 w-8 shrink-0 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-muted-foreground">
                {initials(m)}
              </div>
            )}
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 truncate text-sm text-foreground">
                <span className="truncate">{displayName(m)}</span>
                {isSelf && (
                  <span className="text-xs text-muted-foreground">(You)</span>
                )}
                {m.source === 'auto' && (
                  <span title="Added by an auto-join rule. Will update as their profile changes — pin to keep them regardless.">
                    <Badge variant="gray">Auto</Badge>
                  </span>
                )}
              </p>
              {hasName && m.email && (
                <p className="truncate text-xs text-muted-foreground">{m.email}</p>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: 'role',
      header: 'Role',
      className: 'whitespace-nowrap',
      render: (m: SpaceMembership) => {
        const isSelf = m.userId === user?.userId;
        const isOwnerRow = m.role === 'owner';
        // Caller can change role only if admin+ AND target isn't owner AND target isn't self.
        const canEdit = isAdminPlus && !isOwnerRow && !isSelf;
        if (!canEdit) {
          return <Badge variant={ROLE_BADGE_VARIANT[m.role]}>{m.role}</Badge>;
        }
        return (
          <RadixSelect
            value={m.role}
            onValueChange={(v: string) => handleRoleChange(m, v as SpaceRole)}
            disabled={updateRole.isPending}
          >
            <SelectTrigger className="h-8 w-[110px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="member">Member</SelectItem>
              <SelectItem value="viewer">Viewer</SelectItem>
            </SelectContent>
          </RadixSelect>
        );
      },
    },
    {
      key: 'joined',
      header: 'Joined',
      className: 'whitespace-nowrap',
      render: (m: SpaceMembership) => (
        <span className="text-muted-foreground text-sm">
          {new Date(m.joinedAt).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right whitespace-nowrap w-10',
      render: (m: SpaceMembership) => {
        const isSelf = m.userId === user?.userId;
        const isOwnerRow = m.role === 'owner';
        // Owner can transfer; admin+ can remove non-owner; everyone can remove self.
        const canRemove = isSelf || (isAdminPlus && !isOwnerRow);
        const showTransfer = isOwner && isOwnerRow && !isSelf
          ? false  // owner row but not self → impossible
          : isOwner && !isOwnerRow;
        // Admins can "pin" an auto-joined member to stop rules from managing them.
        const canPin = isAdminPlus && m.source === 'auto' && !isOwnerRow;
        if (!canRemove && !showTransfer && !canPin) return null;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                aria-label="Member actions"
              >
                <MoreVertical className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {showTransfer && (
                <DropdownMenuItem onSelect={() => setTransferOpen(true)}>
                  <ArrowUpRight className="mr-2 h-3.5 w-3.5" />
                  Transfer ownership to this member
                </DropdownMenuItem>
              )}
              {canPin && (
                <DropdownMenuItem onSelect={() => handlePin(m)}>
                  <Pin className="mr-2 h-3.5 w-3.5" />
                  Keep member (stop auto-join)
                </DropdownMenuItem>
              )}
              {canRemove && (
                <DropdownMenuItem
                  onSelect={() => setRemoveTarget(m)}
                  className="text-red-600 focus:text-red-700"
                >
                  <UserMinus className="mr-2 h-3.5 w-3.5" />
                  {isSelf ? 'Leave space' : 'Remove member'}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Members</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            People with access to <span className="font-medium text-foreground">{space?.name ?? 'this space'}</span>.
          </p>
        </div>
        {isAdminPlus && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button>
                Add member
                <ChevronDown className="ml-1.5 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuItem onSelect={() => setAddViaRulesOpen(true)}>
                <Sparkles className="mr-2 h-3.5 w-3.5" />
                <span>
                  Add via auto-join rules
                  <span className="block text-xs text-muted-foreground">
                    Rule-managed; stays in sync with their attributes
                  </span>
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setAddOpen(true)}>
                <UserPlus className="mr-2 h-3.5 w-3.5" />
                <span>
                  Add manually
                  <span className="block text-xs text-muted-foreground">
                    Stays a member regardless of rules
                  </span>
                </span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <Table
        columns={columns}
        data={members}
        keyExtractor={(m) => m.userId}
        emptyMessage={
          isAdminPlus
            ? "No other members yet. Click 'Add Member' to invite someone from your org."
            : "You're the only member."
        }
        isLoading={isLoading}
      />

      {isAdminPlus && (
        <>
          <AddMemberModal
            isOpen={addOpen}
            onClose={() => setAddOpen(false)}
            spaceId={spaceId}
            existingMembers={members}
          />
          <AddViaRulesModal
            isOpen={addViaRulesOpen}
            onClose={() => setAddViaRulesOpen(false)}
            spaceId={spaceId}
            existingMembers={members}
          />
        </>
      )}

      {isOwner && (
        <TransferOwnershipModal
          isOpen={transferOpen}
          onClose={() => setTransferOpen(false)}
          spaceId={spaceId}
          members={members}
          currentOwnerUserId={ownerUserId}
        />
      )}

      <ConfirmModal
        isOpen={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        onConfirm={handleRemove}
        title={removeTarget?.userId === user?.userId ? 'Leave space?' : 'Remove member?'}
        message={
          removeTarget?.userId === user?.userId
            ? `You'll lose access to ${space?.name ?? 'this space'} immediately. An admin would have to add you back.`
            : `Remove ${removeTarget ? displayName(removeTarget) : 'this member'} from ${space?.name ?? 'this space'}? They'll lose access immediately.`
        }
        confirmText={removeTarget?.userId === user?.userId ? 'Leave' : 'Remove'}
        isLoading={removeMember.isPending}
      />
    </div>
  );
}
